import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { type Component, type ComponentProps, createMemo, For } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import {
  type DiagnosticSection,
  getApiStatusCategory,
  getApiStatusLabelKey,
  getSyncStatusCategory,
  getSyncStatusLabelKey,
} from "@aula/shared";
import { Info, RefreshCw, Server, UserCircle, Wifi } from "@fleet/icons";

import { POWERSYNC_URL, SERVER_URL } from "@/lib/runtime-config";
import { appRoute } from "./route";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  GlassSurface,
  PageContainer,
  Separator,
} from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { CopyDiagnosticsButton } from "@/components/copy-diagnostics-button";
import { useTheme } from "@/providers/theme-provider";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";

function DiagnosticRow(props: {
  label: string;
  value: string;
  status: "success" | "warning" | "danger" | "neutral";
  icon: Component<ComponentProps<"svg"> & { size?: number | string }>;
}) {
  const getStatusColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500 bg-green-500/10";
      }
      case "warning": {
        return "text-yellow-500 bg-yellow-500/10";
      }
      case "danger": {
        return "text-red-500 bg-red-500/10";
      }
      case "neutral": {
        return "text-muted-foreground bg-muted/10";
      }
      default: {
        return "text-muted-foreground bg-muted/10";
      }
    }
  };

  const getTextColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500";
      }
      case "warning": {
        return "text-yellow-500";
      }
      case "danger": {
        return "text-red-500";
      }
      case "neutral": {
        return "text-muted-foreground";
      }
      default: {
        return "text-muted-foreground";
      }
    }
  };

  return (
    <div class="flex items-center justify-between gap-4 py-2">
      <div class="flex items-center gap-3">
        <div
          class={`flex h-8 w-8 items-center justify-center rounded-full ${getStatusColorClass()}`}
        >
          <props.icon size={16} />
        </div>
        <span class="text-sm font-medium">{props.label}</span>
      </div>
      <span class={`text-sm font-bold shrink text-right truncate ${getTextColorClass()}`}>
        {props.value}
      </span>
    </div>
  );
}

/**
 * The only thing the UI adds and the report omits: the icon. It lives outside
 * the model so the model remains serializable and shared.
 */
const ICON_BY_KEY: Record<string, typeof Info> = {
  online: Wifi,
  session: UserCircle,
  api: Server,
  sync: RefreshCw,
};

function DebugPage() {
  const { connectivityRef } = usePowerSyncContext();
  const state = fromActorRef(connectivityRef);
  const { t, tDynamic } = useI18n();

  const context = createMemo(() => state().context);
  const stateValue = createMemo(() => state().value);

  const isRetryInProgress = createMemo(
    () => stateValue() === "checking" || stateValue() === "manualChecking",
  );

  const handleForceRetry = () => {
    connectivityRef.send({ type: "RETRY" });
  };

  const getApiLabel = () =>
    tDynamic(getApiStatusLabelKey((v) => state().matches(v), context().apiStatus));
  const getApiState = () => getApiStatusCategory((v) => state().matches(v), context().apiStatus);
  const getSyncLabel = () =>
    tDynamic(getSyncStatusLabelKey((v) => state().matches(v), context().syncStatus));
  const getSyncState = () => getSyncStatusCategory((v) => state().matches(v), context().syncStatus);

  const canRetry = createMemo(() => state().can({ type: "RETRY" }));

  const theme = useTheme();
  const session = useSession();

  /**
   * SINGLE SOURCE for the screen: the UI renders this and the button copies it.
   * Previously the screen had its rows and the report had its own object — two
   * sources for the same thing, so adding a row here left the report
   * incomplete without anyone noticing.
   *
   * `key` is stable and language-independent (logic uses it), `label` and
   * `value` are already translated, `status` is only used by the UI, and `raw`
   * is used by logic so it does not depend on a translation.
   */
  /** Truncated user ID — never whole: the report gets pasted in chats. */
  const truncatedId = () => {
    const id = session.userId();
    return id ? `${id.slice(0, 8)}…` : "-";
  };

  // NAMED sections, not indexed: `sections()[1]` coupled the screen to the
  // array order, and inserting one would silently break it.
  const connectivity = (): DiagnosticSection => ({
    title: t(msg`Connectivity`),
    items: [
      {
        key: "online",
        label: t(msg`Internet Access`),
        value: context().isOnline ? t(msg`Connected`) : t(msg`Disconnected`),
        status: context().isOnline ? "success" : "danger",
        raw: context().isOnline,
      },
      {
        key: "session",
        label: t(msg`User Session`),
        value: context().isAuthenticated ? t(msg`Active`) : t(msg`None`),
        status: context().isAuthenticated ? "success" : "warning",
        raw: context().isAuthenticated,
      },
      {
        key: "api",
        label: t(msg`API Server (Writes)`),
        value: getApiLabel(),
        status: getApiState(),
        raw: context().apiStatus,
      },
      {
        key: "clientVersion",
        label: t(msg`Client Version`),
        value: `${import.meta.env.APP_VERSION} (${import.meta.env.COMMIT_HASH})`,
        status: "neutral",
      },
      ...(context().serverVersion
        ? [
            {
              key: "serverVersion",
              label: t(msg`Server Version`),
              value: String(context().serverVersion),
              status: "neutral" as const,
            },
          ]
        : []),
      {
        key: "sync",
        label: t(msg`PowerSync (Sync)`),
        value: getSyncLabel(),
        status: getSyncState(),
        // `raw` carries the aggregate state; the machine's CRUD state is omitted
        // as a row because its label ("Status") is not translated in any catalog,
        // and adding it would display it in English across all eleven languages.
        // This is the same criterion applied to the other strings and is pending
        // the i18n pass.
        raw: context().syncStatus,
      },
    ],
  });

  const system = (): DiagnosticSection => ({
    title: t(msg`System Information`),
    items: [
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- technical value
      { key: "adapter", label: t(msg`Adapter`), value: "WASM + OPFS" },
      { key: "serverUrl", label: t(msg`Server URL`), value: SERVER_URL },
      { key: "powersyncUrl", label: t(msg`PowerSync URL`), value: POWERSYNC_URL },
      {
        key: "mode",
        label: t(msg`Mode`),
        value: import.meta.env.DEV ? "development" : "production",
      },
      // The EFFECTIVE mode alongside the device mode: their divergence causes
      // "text in the opposite mode" failures, and without both values the report
      // does not allow identifying it.
      {
        key: "colorMode",
        label: t(msg`Appearance`),
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- technical value
        value: `${theme.colorMode()} · ${theme.tokens()?.id ?? "-"} (${theme.isDark() ? "dark" : "light"} / device ${theme.systemIsDark() ? "dark" : "light"})`,
      },
      {
        key: "account",
        label: t(msg`Account Information`),
        // Truncated: cross-referenceable with server logs, but pasteable in a chat.
        value: truncatedId(),
      },
    ],
  });

  /** What is displayed AND what is copied. One place. */
  const sections = (): DiagnosticSection[] => [connectivity(), system()];

  return (
    <div class="flex flex-1 flex-col overflow-hidden">
      <PageSubHeader title={t(msg`Diagnostics`)} />

      <PageContainer maxWidth="lg" class="space-y-6" containerClass="min-h-0">
        {/* Entrance animation is provided once at the route Outlet
            (app-layout-content). This inner wrapper only handles spacing. */}
        <div class="space-y-6">
          <GlassSurface class="p-4">
            <h2 class="mb-4 text-lg font-semibold">{connectivity().title}</h2>
            <div class="space-y-1">
              {/* Renders the SAME model that the button copies. Adding a row
                  above makes it appear here and in the report at the same time. */}
              <For each={connectivity().items}>
                {(item) => (
                  <DiagnosticRow
                    label={item.label}
                    value={item.value}
                    status={item.status ?? "neutral"}
                    icon={ICON_BY_KEY[item.key] ?? Info}
                  />
                )}
              </For>
            </div>

            <Button
              variant="default"
              class="mt-6 w-full gap-2"
              onClick={handleForceRetry}
              disabled={isRetryInProgress() || !canRetry()}
            >
              <RefreshCw size={18} class={isRetryInProgress() ? "animate-spin" : ""} />
              {t(msg`Force connection retry`)}
            </Button>

            <div class="mt-3">
              <CopyDiagnosticsButton appName="aula" sections={sections} />
            </div>
          </GlassSurface>

          <Card>
            <CardHeader>
              <CardTitle>{system().title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div class="space-y-3">
                <For each={system().items}>
                  {(item) => (
                    <>
                      <div class="flex items-center justify-between text-sm">
                        <span class="text-muted-foreground">{item.label}</span>
                        <Badge variant="outline" class="font-mono text-xs">
                          {item.value}
                        </Badge>
                      </div>
                      <Separator />
                    </>
                  )}
                </For>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </div>
  );
}

export const debugRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/debug",
  component: DebugPage,
});
